import ReactPaginate, { ReactPaginateProps } from "react-paginate";
import clsx from "clsx";
import { ArrowLeftIcon, ArrowRightIcon } from "./icons";

import { usePaginationContext } from "./hooks/usePaginationContext";

type Page = Parameters<NonNullable<React.ComponentProps<typeof ReactPaginate>["onPageChange"]>>[0];
type PaginationComponentProps = Pick<
  ReactPaginateProps,
  | "activeClassName"
  | "breakClassName"
  | "breakLinkClassName"
  | "containerClassName"
  | "nextClassName"
  | "nextLinkClassName"
  | "pageClassName"
  | "pageLinkClassName"
  | "previousLinkClassName"
  | "previousClassName"
> & {
  pageButtonClasses?: string;
  wrapperClassName?: string;
};

export const Pagination = ({
  activeClassName,
  breakClassName,
  breakLinkClassName,
  containerClassName,
  nextClassName,
  nextLinkClassName,
  pageClassName,
  pageLinkClassName,
  previousClassName,
  previousLinkClassName,
  pageButtonClasses,
  wrapperClassName,
}: PaginationComponentProps) => {
  const {
    onPageChange,
    pageCount,
    currentPageInternal,
    setCurrentPageInternal,
    pageRangeDisplayed = 5,
  } = usePaginationContext();

  const handlePageClick = (page: Page) => {
    setCurrentPageInternal(page.selected);
    onPageChange(page.selected);
  };
  pageButtonClasses ??= clsx("min-w-[32px] h-[32px] overflow-hidden", "flex justify-stretch items-stretch");
  return (
    <div className={clsx("text-neutral-600 flex items-baseline gap-2", wrapperClassName ?? "")}>
      <ReactPaginate
        forcePage={currentPageInternal}
        breakLabel="..."
        previousLabel={<ArrowLeftIcon />}
        nextLabel={<ArrowRightIcon />}
        marginPagesDisplayed={1}
        onPageChange={handlePageClick}
        pageRangeDisplayed={pageRangeDisplayed}
        pageCount={pageCount}
        renderOnZeroPageCount={null}
        containerClassName={clsx("flex list-none", containerClassName)}
        pageLinkClassName={clsx("p-1 flex flex-1 justify-center items-center hover:bg-blue-100", pageLinkClassName)}
        previousLinkClassName={clsx(
          clsx("p-1 flex flex-1 justify-center items-center", {
            "hover:bg-blue-100": currentPageInternal !== 0,
          }),
          previousLinkClassName
        )}
        nextLinkClassName={clsx(
          clsx("p-1 flex flex-1 justify-center items-center", {
            "hover:bg-blue-100": currentPageInternal !== pageCount - 1,
          }),
          nextLinkClassName
        )}
        breakLinkClassName={clsx("p-1 flex flex-1 justify-center items-center", breakLinkClassName)}
        activeClassName={clsx(clsx("text-text-base-secondary bg-platform-100"), activeClassName)}
        pageClassName={clsx(pageButtonClasses, pageClassName)}
        breakClassName={clsx(pageButtonClasses, breakClassName)}
        nextClassName={clsx(
          clsx(pageButtonClasses, {
            "text-neutral-400 pointer-events-none": currentPageInternal === pageCount - 1,
          }),
          nextClassName
        )}
        previousClassName={clsx(
          clsx(pageButtonClasses, {
            "text-neutral-400 pointer-events-none": currentPageInternal === 0,
          }),
          previousClassName
        )}
      />
    </div>
  );
};
